/* agentSpecThunk.ts */

/* Redux-Toolkit */
import { createAsyncThunk } from "@reduxjs/toolkit";

/* Axios */
import { HttpStatusCode, isAxiosError } from "axios";
import agentAxiosInterceptor from "src/utils/helper/agent-axios-interceptor";

/* Constants */
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";

/* Interfaces */
import { AgentChatMessage } from "src/shared/interfaces/api/AgentChat";

/* Interfaces */
import {
  AGENT_SPEC_TIMEOUT_MS,
  AGENT_SPEC_VERSION,
  AgentAnswerLanguage,
  AgentCurrentState,
  AgentSpecResponse,
} from "src/shared/interfaces/api/AgentSpec";

interface RequestAgentSpecParams {
  prompt: string;
  model: string | null;
  /* The language the assistant should ask a question in. */
  answerLanguage: AgentAnswerLanguage;
  currentState: AgentCurrentState;
  /* The exchange so far, so a follow-up completes the earlier request. */
  history: AgentChatMessage[];
}

/**
 * Ask the insights-agent to translate a prompt into a
 * configuration spec.
 *
 * Rejects with a message ready to show the user: the agent's own
 * `detail` when it sent one, otherwise a mapping of the status.
 * A missing response (network error or the 30 s timeout) is
 * reported as unavailable rather than as a failed prompt.
 */
export const requestAgentSpec = createAsyncThunk<
  AgentSpecResponse,
  RequestAgentSpecParams,
  { rejectValue: string }
>(
  "agentChat/requestAgentSpec",
  async (params, { rejectWithValue, signal }) => {
    try {
      const response =
        await agentAxiosInterceptor.post<AgentSpecResponse>(
          "/spec/",
          {
            prompt: params.prompt,
            model: params.model ?? undefined,
            answer_language: params.answerLanguage,
            current_state: params.currentState,
            history: params.history,
          },
          {
            timeout: AGENT_SPEC_TIMEOUT_MS,
            /* Lets the panel abandon a request the user cancelled, rather
               than applying a spec they are no longer waiting for. */
            signal,
          }
        );

      /**
       * A newer contract may set fields this frontend does not know how
       * to apply, so a spec is refused rather than applied in part.
       * Older versions are accepted: the contract only grows in ways
       * that stay safe to ignore, and a rollback of the agent must not
       * take the assistant down with it.
       */
      if (response.data.spec_version > AGENT_SPEC_VERSION) {
        return rejectWithValue(
          ErrorMessage.AGENT_VERSION_MISMATCH
        );
      }

      return response.data;
    } catch (error) {
      /* No branch for cancellation: redux-toolkit rejects an aborted thunk
         with its own AbortError as soon as the signal fires, and marks the
         action `meta.aborted` — which is what the slice reads. */
      if (!isAxiosError(error) || !error.response) {
        return rejectWithValue(
          ErrorMessage.AGENT_UNAVAILABLE
        );
      }

      const detail = (
        error.response.data as { detail?: unknown }
      )?.detail;
      if (typeof detail === "string") {
        return rejectWithValue(detail);
      }

      if (
        error.response.status === HttpStatusCode.Forbidden
      ) {
        return rejectWithValue(ErrorMessage.NO_AGENT_ACCESS);
      }
      if (
        error.response.status ===
        HttpStatusCode.ServiceUnavailable
      ) {
        return rejectWithValue(
          ErrorMessage.AGENT_UNAVAILABLE
        );
      }
      return rejectWithValue(ErrorMessage.UNEXPECTED_ERROR);
    }
  }
);
